class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.moneyFormat = this.dataset.moneyFormat;
    this.sectionId = this.dataset.section;

    // Dawn replaces input[name="id"] via section rendering — delegate to document
    document.addEventListener('change', this._onVariantChange = (e) => {
      if (e.target.name !== 'id') return;
      if (e.target.closest('form')?.id !== `product-form-${this.sectionId}`) return;
      const variant = this.variantData.find((v) => v.id == e.target.value);
      if (variant) this.updatePrices(variant);
    });

    // Before the first updatePrices: the per-unit state has to exist for the
    // tier totals to be summed from it instead of from the main variant.
    this.initUnitSelectors();

    // Initialize prices on page load
    const initInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
    const currentVariant = this.variantData.find((v) => v.id == parseInt(initInput?.value));
    if (currentVariant) this.updatePrices(currentVariant);

    this.hiddenQty = document.getElementById(`qb-hidden-qty-${this.sectionId}`);

    // Update main price when tier selection changes + apply discount code + sync hidden quantity input
    this.addEventListener('change', (e) => {
      if (e.target.name !== 'qb_tier') return;
      if (this.hiddenQty) this.hiddenQty.value = e.target.value;
      const label = this.querySelector(`label[for="${e.target.id}"]`);
      if (!label?.dataset.calculatedPrice) return;
      this.updateMainPrice(
        parseInt(label.dataset.calculatedPrice),
        parseInt(label.dataset.calculatedCompare)
      );
      const code = label.dataset.discountCode;
      if (code) fetch(`/discount/${encodeURIComponent(code)}`);
    });

    // Sync hidden quantity + apply discount code for preselected tier on page load
    const checked = this.querySelector('input[name="qb_tier"]:checked');
    if (checked) {
      if (this.hiddenQty) this.hiddenQty.value = checked.value;
      const lbl = this.querySelector(`label[for="${checked.id}"]`);
      const code = lbl?.dataset.discountCode;
      if (code) fetch(`/discount/${encodeURIComponent(code)}`);
    }
  }

  /*
    Per-unit variant selectors (snippets/dd0-quantity-break-variants.liquid).
    selectedVariants holds one variant id PER UNIT, per tier:
      { option_1: [id], option_2: [id, id], ... }
    Absent when the merchant leaves the setting off — every method below then
    no-ops and the block behaves exactly as it did before.
  */
  initUnitSelectors() {
    this.selectedVariants = {};

    this.querySelectorAll('.quantity-break__variants').forEach((group) => {
      this.selectedVariants[group.dataset.tier] = Array.from(
        group.querySelectorAll('.quantity-break__unit'),
        (unit) => parseInt(unit.dataset.selectedId, 10)
      );
    });

    if (!Object.keys(this.selectedVariants).length) return;

    this.addEventListener('change', (e) => {
      const unit = e.target.closest('.quantity-break__unit');
      if (unit) this.onUnitChange(unit);
    });

    // Swatch display: the buttons write to the hidden <select> and let the
    // change handler above do the rest, so both displays share one code path.
    this.addEventListener('click', (e) => {
      const button = e.target.closest('.quantity-break__swatch');
      if (!button) return;
      const select = button.closest('.quantity-break__option')?.querySelector('select');
      if (!select || select.value === button.dataset.value) return;
      select.value = button.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('.quantity-break__unit').forEach((unit) => this.updateUnitAvailability(unit));
  }

  onUnitChange(unit) {
    const group = unit.closest('.quantity-break__variants');
    const values = Array.from(unit.querySelectorAll('select'), (s) => s.value);
    // same resolver as component-bundle-offer.js:83-85
    const variant = this.variantData.find((v) => !v.options.some((opt, i) => values[i] !== opt));

    if (variant) {
      this.selectedVariants[group.dataset.tier][parseInt(unit.dataset.unitIndex, 10)] = variant.id;
      unit.dataset.selectedId = variant.id;
    }

    this.updateUnitAvailability(unit);

    // Picking a variant inside a tier selects that tier. The <label> does not do
    // it for us: label activation is skipped for interactive descendants.
    const radio = document.getElementById(group.closest('label')?.getAttribute('for') || '');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.mainVariant) this.updatePrices(this.mainVariant);
  }

  updateUnitAvailability(unit) {
    const selects = Array.from(unit.querySelectorAll('select'));
    const values = selects.map((s) => s.value);

    selects.forEach((select, i) => {
      Array.from(select.options).forEach((option) => {
        const combo = values.slice();
        combo[i] = option.value;
        const match = this.variantData.find((v) => !v.options.some((opt, k) => combo[k] !== opt));
        option.disabled = !match || !match.available;
      });

      // Mirror the <select> onto its swatches: same source of truth, so the
      // swatch display can never disagree with what will be added to the cart.
      select.closest('.quantity-break__option')
        ?.querySelectorAll('.quantity-break__swatch')
        .forEach((button) => {
          const option = Array.from(select.options).find((o) => o.value === button.dataset.value);
          button.disabled = !!option?.disabled;
          button.setAttribute('aria-pressed', String(select.value === button.dataset.value));
        });
    });
  }

  /*
    Returns the cart lines for the selected tier as [{ id, quantity }], grouping
    repeated variants into a single line, or null when there is nothing special
    to send — product-form.js then keeps its normal single-line flow.
  */
  getCartItems() {
    if (!this.selectedVariants || !Object.keys(this.selectedVariants).length) return null;

    const checked = this.querySelector('input[name="qb_tier"]:checked');
    if (!checked) return null;

    const tier = this.querySelector(`label[for="${checked.id}"] .quantity-break__variants`)?.dataset.tier;
    const ids = tier && this.selectedVariants[tier];
    if (!ids || !ids.length) return null;

    return ids.reduce((items, id) => {
      const line = items.find((item) => item.id === id);
      if (line) line.quantity += 1;
      else items.push({ id, quantity: 1 });
      return items;
    }, []);
  }

  updatePrices(variant) {
    this.mainVariant = variant;
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;

    this.querySelectorAll('label[data-quantity]').forEach((label) => {
      const qty = parseInt(label.dataset.quantity);
      const pctLeft = parseFloat(label.dataset.percentageLeft);
      const fixedOff = parseInt(label.dataset.fixedDiscount);
      const compareType = label.dataset.compareType;

      // With per-unit selectors the tier costs the sum of the variants actually
      // chosen, not the main variant times the quantity. The discount still
      // applies to the whole tier, after the sum.
      const tier = label.querySelector('.quantity-break__variants')?.dataset.tier;
      const unitIds = tier && this.selectedVariants?.[tier];

      let subtotal;
      let compareSubtotal;

      if (unitIds && unitIds.length) {
        subtotal = 0;
        compareSubtotal = 0;
        unitIds.forEach((id) => {
          const unitVariant = this.variantData.find((v) => v.id === id) || variant;
          subtotal += unitVariant.price;
          compareSubtotal +=
            compareType === 'compare_price' && unitVariant.compare_at_price > unitVariant.price
              ? unitVariant.compare_at_price
              : unitVariant.price;
        });
      } else {
        const baseCompare =
          compareType === 'compare_price' && compareAtPrice && compareAtPrice > price
            ? compareAtPrice
            : price;
        subtotal = price * qty;
        compareSubtotal = baseCompare * qty;
      }

      const totalPrice = Math.round(subtotal * pctLeft - fixedOff);
      const comparePrice = compareSubtotal;

      label.dataset.calculatedPrice = totalPrice;
      label.dataset.calculatedCompare = comparePrice;

      const saved = comparePrice - totalPrice;
      const savedRounded = Math.round(saved / 100) * 100;
      const priceEach = Math.round(totalPrice / qty);
      const comparePriceEach = Math.round(comparePrice / qty);
      const percentage = comparePrice > 0 && saved > 0
        ? Math.floor((saved / comparePrice) * 100)
        : 0;

      label.querySelectorAll('.variant-price-update[data-text]').forEach((el) => {
        el.innerHTML = this.renderText(el.dataset.text, {
          qty,
          totalPrice,
          comparePrice,
          saved,
          savedRounded,
          priceEach,
          comparePriceEach,
          percentage,
        });
      });

      label
        .querySelectorAll('.quantity-break__compare-price')
        .forEach((el) => el.classList.toggle('hidden', comparePrice <= totalPrice));
    });

    // Update main price for currently selected tier
    // NOTE: the tier radios are name="qb_tier"; name="quantity" is the hidden
    // input at the end of the snippet, which can never be :checked.
    const checkedInput = this.querySelector('input[name="qb_tier"]:checked');
    if (checkedInput) {
      const selectedLabel = this.querySelector(`label[for="${checkedInput.id}"]`);
      if (selectedLabel?.dataset.calculatedPrice) {
        this.updateMainPrice(
          parseInt(selectedLabel.dataset.calculatedPrice),
          parseInt(selectedLabel.dataset.calculatedCompare)
        );
      }
    }
  }

  updateMainPrice(price, comparePrice) {
    const priceBlock = document.getElementById(`price-${this.sectionId}`);
    if (priceBlock) {
      const priceInner = priceBlock.querySelector('.price') || priceBlock;
      const onSale = comparePrice > price;
      priceInner.classList.toggle('price--on-sale', onSale);

      const badge = priceBlock.querySelector('.price__badge-sale');
      if (badge && onSale) {
        const tmpl = badge.dataset.badgeTemplate || '';
        const diff = comparePrice - price;
        const pct = comparePrice > 0 ? Math.floor((diff / comparePrice) * 100) : 0;
        badge.textContent = tmpl
          .replace('[percentage]', pct > 0 ? pct + '%' : '')
          .replace('[amount]', this.formatMoney(diff));
      }

      const salePriceEl = priceBlock.querySelector('.price__sale .price-item--sale');
      if (salePriceEl) salePriceEl.innerHTML = this.formatMoney(price);

      const compareEl = priceBlock.querySelector('.price__sale .price-item--regular');
      if (compareEl) compareEl.innerHTML = this.formatMoney(comparePrice);

      const regularEl = priceBlock.querySelector('.price__regular .price-item--regular');
      if (regularEl) regularEl.innerHTML = this.formatMoney(price);
    }

    // Update ATC button price/compare
    const atcBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (atcBtn) {
      const atcPriceEl = atcBtn.querySelector('.atc-price');
      const atcCompareEl = atcBtn.querySelector('.atc-compare');
      if (atcPriceEl) atcPriceEl.innerHTML = this.formatMoney(price);
      if (atcCompareEl) {
        if (comparePrice > price) {
          atcCompareEl.innerHTML = this.formatMoney(comparePrice);
          atcCompareEl.style.display = '';
        } else {
          atcCompareEl.style.display = 'none';
        }
      }
    }
  }

  renderText(template, { qty, totalPrice, comparePrice, saved, savedRounded, priceEach, comparePriceEach, percentage }) {
    const tokens = {
      '[quantity]': qty,
      '[price]': `<span>${this.formatMoney(totalPrice)}</span>`,
      '[compare_price]': `<span>${this.formatMoney(comparePrice)}</span>`,
      '[amount_saved]': `<span>${this.formatMoney(saved)}</span>`,
      '[amount_saved_rounded]': `<span>${this.formatMoney(savedRounded)}</span>`,
      '[price_each]': `<span>${this.formatMoney(priceEach)}</span>`,
      '[compare_price_each]': `<span>${this.formatMoney(comparePriceEach)}</span>`,
      '[percentage]': percentage > 0 ? `<span>${percentage}%</span>` : '',
    };
    return Object.entries(tokens).reduce((t, [k, v]) => t.replaceAll(k, String(v)), template);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }
  disconnectedCallback() {
    document.removeEventListener('change', this._onVariantChange);
  }
}

customElements.define('quantity-breaks', QuantityBreaks);
